interface MetricValue {
  entityId: string;
  entityName: string;
  value: number | string;
  rawValue?: number; // For comparison when display value is formatted
}

interface MetricCardProps {
  title: string;
  values: MetricValue[];
  unit?: string;
  highlightBest?: boolean;
  highlightWorst?: boolean;
  formatValue?: (value: number | string) => string;
  higherIsBetter?: boolean; // default true
}

export function MetricCard({
  title,
  values,
  unit = "",
  highlightBest = true,
  highlightWorst = false,
  formatValue,
  higherIsBetter = true,
}: MetricCardProps) {
  // Find best and worst performers
  const numericValues = values
    .map((v) => ({
      ...v,
      numValue:
        typeof v.rawValue !== "undefined"
          ? v.rawValue
          : typeof v.value === "number"
            ? v.value
            : parseFloat(v.value as string),
    }))
    .filter((v) => !isNaN(v.numValue));

  if (numericValues.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-400 dark:text-gray-500">No data</p>
      </div>
    );
  }

  const sortedValues = [...numericValues].sort((a, b) =>
    higherIsBetter ? b.numValue - a.numValue : a.numValue - b.numValue,
  );

  const bestEntityId = highlightBest ? sortedValues[0]?.entityId : null;
  const worstEntityId = highlightWorst
    ? sortedValues[sortedValues.length - 1]?.entityId
    : null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
        {title}
      </h3>
      <div className="space-y-2">
        {values.map((v) => {
          const isBest = highlightBest && v.entityId === bestEntityId;
          const isWorst = highlightWorst && v.entityId === worstEntityId;

          const displayValue = formatValue
            ? formatValue(v.value)
            : `${v.value}${unit}`;

          return (
            <div
              key={v.entityId}
              className={`flex items-center justify-between p-2 rounded ${
                isBest
                  ? "bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700"
                  : isWorst
                    ? "bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700"
                    : "bg-gray-50 dark:bg-gray-700/50"
              }`}
            >
              <div className="flex items-center gap-2">
                {isBest && <span className="text-lg">🥇</span>}
                {isWorst && <span className="text-lg">🔴</span>}
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {v.entityName}
                </span>
              </div>
              <span
                className={`text-sm font-semibold ${
                  isBest
                    ? "text-green-700 dark:text-green-400"
                    : isWorst
                      ? "text-red-700 dark:text-red-400"
                      : "text-gray-900 dark:text-white"
                }`}
              >
                {displayValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
